import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VideoFeatures } from '@/store/videoFeatures';
import { AutoplayMetricsOverview } from './metrics/AutoplayMetricsOverview';
import { AutoplayRetentionChart } from './metrics/AutoplayRetentionChart';
import { useAutoplayTestMetrics } from '@/hooks/useAutoplayTest';
import { Loader2 } from 'lucide-react';

interface AutoplayMetricsTabProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const AutoplayMetricsTab: React.FC<AutoplayMetricsTabProps> = ({
  features,
  setFeature
}) => {
  useLanguage();
  const abTestConfig = features.autoplayOverlay?.abTestConfig;
  const testStatus = abTestConfig?.status;
  // Quando o teste foi finalizado/excluído, o testId pode permanecer no estado
  // mas a rota de métricas não deve mais ser chamada — o teste não existe
  // mais no backend.
  const testExists = !!abTestConfig?.testId && testStatus !== 'finished';
  const testId = testExists ? abTestConfig?.testId : undefined;
  const isTestActive = testStatus === 'active';

  // Buscar métricas do backend com auto-refetch a cada 10s se o teste estiver ativo
  const { data: metricsData, isLoading, isRefetching, refetch } = useAutoplayTestMetrics(testId, {
    refetchInterval: isTestActive ? 10000 : undefined
  });

  return (
    <div className="py-4 space-y-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-foreground">
              {translateInterfaceText("Métricas do Teste A/B")}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {translateInterfaceText("Resultados em tempo real das variações de autoplay")}</p>
          </div>
          {isRefetching && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>{translateInterfaceText("Atualizando...")}</span>
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="metrics" className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-11 bg-[#F1F5F9] p-1 rounded-xl" style={{ minWidth: '300px' }}>
          <TabsTrigger
            value="metrics"
            className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
          >
            {translateInterfaceText("Métricas")}</TabsTrigger>
          <TabsTrigger
            value="retention"
            className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
          >
            {translateInterfaceText("Retenção")}</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="mt-6">
          <AutoplayMetricsOverview
            features={features}
            setFeature={setFeature}
            metricsData={metricsData}
            isLoading={isLoading}
            onPitchSaved={() => refetch()}
          />
        </TabsContent>

        <TabsContent value="retention" className="mt-6">
          <AutoplayRetentionChart
            features={features}
            metricsData={metricsData}
            isLoading={isLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
